import { Injectable } from '@angular/core';
import { FlyoutState } from '@core/states/flyout.state';
import { ArrayHelpersService, AutoTableRepositoryFactory } from '@yourcause/common';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { SpinnerService } from './spinner.service';

@AttachYCState(FlyoutState)
@Injectable({ providedIn: 'root' })
export class GcFlyoutService extends BaseYCService<FlyoutState> {

  get currentIdForFlyout () {
    return this.get('currentIdForFlyout');
  }

  get idsForFlyout () {
    return this.get('idsForFlyout');
  }

  constructor (
    private arrayHelper: ArrayHelpersService,
    private spinnerService: SpinnerService,
    private autoRepoFactory: AutoTableRepositoryFactory
  ) {
    super();
  }

  /**
   * Set current ID for flyout
   *
   * @param id: ID to set
   */
  setCurrentIdForFlyout (id: string|number) {
    this.set('currentIdForFlyout', id);
  }

  /**
   * Set IDs for Flyout
   *
   * @param ids: IDs
   */
  setIdsForFlyout (ids: (number|string)[]) {
    this.set('idsForFlyout', ids);
  }

  /**
   * Navigate to the next flyout record
   */
  onNextFlyoutRecord = async (resolver?: (id: string|number) => Promise<void>) => {
    const nextId = this.arrayHelper.getNextRecordId(
      this.currentIdForFlyout,
      this.idsForFlyout
    );
    if (!!resolver) {
      this.spinnerService.startSpinner();
      await resolver(nextId);
      this.spinnerService.stopSpinner();
    }
    this.setCurrentIdForFlyout(nextId);
  };

  /**
   * Navigate to the previous flyout record
   */
  onPreviousFlyoutRecord = async (resolver?: (id: string|number) => Promise<void>) => {
    const previousId = this.arrayHelper.getPreviousRecordId(
      this.currentIdForFlyout,
      this.idsForFlyout
    );
    if (!!resolver) {
      await resolver(previousId);
    }
    this.setCurrentIdForFlyout(previousId);
  };

  /**
   * Sets all the info needed for the flyout
   *
   * @param record: Record to set flyout info for
   */
  setInfoForFlyout<T> (
    record: T,
    tableKey: string,
    idAttribute: keyof T
  ) {
    const repo = this.autoRepoFactory.getRepository(tableKey);
    const recordIds = (repo.clientSideFilteredRows as T[]).map((row) => {
      return row[idAttribute];
    }).filter((value) => {
      return !!value;
    }) as (string|number)[];
    this.setIdsForFlyout(recordIds);
    this.setCurrentIdForFlyout(record[idAttribute] as string|number);
  }

}
